import {
  EVIDENCE_LEGEND,
  buildCallChain,
  dedupeInvokes,
  evidenceLabels,
  formatMatchedApi,
  formatMethodRef,
  mergeCrimeLocations,
} from "./crimesUtils";

import "./crimeDetailsStyles.scss";

// One component for both the live Crimes tab and the export/PDF variant, so the
// two can never drift apart. The report passes tighter caps and leaves the raw
// smali out (showInvokes={false}) — it is the bulk of the volume and the report
// is meant to stay compact.
const CrimeDetailsView = ({
  crime,
  showInvokes = false,
  showLegend = false,
  maxLocations = 5,
  maxChainSteps = 6,
  maxInvokes = 5,
}) => {
  const matchedApis = Array.isArray(crime?.matched_api) ? crime.matched_api : [];
  const permissions = Array.isArray(crime?.permissions) ? crime.permissions : [];
  const locations = mergeCrimeLocations(crime);

  if (matchedApis.length === 0 && locations.length === 0 && permissions.length === 0) {
    return <p className="crime-details__empty">No details found</p>;
  }

  const visibleLocations = locations.slice(0, maxLocations);
  const hiddenLocations = locations.length - visibleLocations.length;

  return (
    <div className="crime-details">
      {matchedApis.length > 0 ? (
        <div className="crime-details__section">
          <h4 className="crime-details__section-title">Matched APIs</h4>
          <ol className="crime-details__api-list">
            {matchedApis.map((api, index) => (
              <li className="crime-details__api" key={index}>
                {formatMatchedApi(api)}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {permissions.length > 0 ? (
        <div className="crime-details__section">
          <h4 className="crime-details__section-title">Permissions</h4>
          <p className="crime-details__permissions">{permissions.join(", ")}</p>
        </div>
      ) : null}

      {visibleLocations.length > 0 ? (
        <div className="crime-details__section">
          <h4 className="crime-details__section-title">Detected in</h4>
          {/* The report prints one legend above the whole list instead, so it
              does not repeat for every action. */}
          {showLegend ? <p className="crime-details__legend">{EVIDENCE_LEGEND}</p> : null}

          {visibleLocations.map((location, index) => (
            <div className="crime-details__location" key={index}>
              <p className="crime-details__location-head">
                <span className="crime-details__location-name">
                  {formatMethodRef(location.parent)}
                </span>
                {evidenceLabels(location).map((label) => (
                  <span className="crime-details__evidence" key={label}>
                    {label}
                  </span>
                ))}
              </p>

              {location.callGraph.map((node, nodeIndex) => {
                const chain = buildCallChain(node?.path);
                const invokes = showInvokes
                  ? dedupeInvokes(
                      (Array.isArray(node?.path) ? node.path : []).flatMap(
                        (step) => step?.invoke_at || [],
                      ),
                    )
                  : [];

                return (
                  <div className="crime-details__call" key={nodeIndex}>
                    <p className="crime-details__call-api">{formatMethodRef(node?.api)}</p>

                    <p className="crime-details__chain">
                      {chain.direct
                        ? "called directly here"
                        : // A text arrow on purpose: the pdf stylesheet hides svg
                          // inside these blocks, so an icon would vanish on paper.
                          `reached through ${chain.via.slice(0, maxChainSteps).join(" → ")}${
                            chain.via.length > maxChainSteps ? " → …" : ""
                          }`}
                    </p>

                    {invokes.slice(0, maxInvokes).map((invoke, invokeIndex) => (
                      <p className="crime-details__invoke" key={invokeIndex}>
                        <span className="crime-details__invoke-kind">invoke-{invoke.kind}</span>
                        {invoke.registers.length ? (
                          <span className="crime-details__invoke-registers">
                            {invoke.registers.join(", ")}
                          </span>
                        ) : null}
                        <span className="crime-details__invoke-target">{invoke.target}</span>
                        {invoke.count > 1 ? (
                          <span className="crime-details__invoke-count">×{invoke.count}</span>
                        ) : null}
                      </p>
                    ))}

                    {invokes.length > maxInvokes ? (
                      <p className="crime-details__more">
                        +{invokes.length - maxInvokes} more call sites
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}

          {hiddenLocations > 0 ? (
            <p className="crime-details__more">
              +{hiddenLocations} more {hiddenLocations === 1 ? "location" : "locations"}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default CrimeDetailsView;
